import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { GenericValue } from '../../../core/model/generic-value';
import { HisUiService } from '../../../core/service/base/his-ui.service';
import { HisOption } from '../../../genericsearch/model/his-option';
import { CommunicationLogUiText } from '../../communicationlog-uitexts';
import { ConversationRecordEditData } from '../../model/communication-log-edit-data.model';
import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { HisFormUtils } from '../../../core/utils';

@Component( {
    selector: 'his-conversation-record-edit',
    templateUrl: './conversation-record-edit.component.html',
    styleUrl: './conversation-record-edit.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class ConversationRecordEditComponent implements OnInit {
    private uiService = inject(HisUiService);
    private notificationService = inject(HisNotificationService);
    private formBuilder = inject(FormBuilder);

    @Input( { required: true } ) availableDomains: GenericValue[];
    @Output() conversationRecord: EventEmitter<ConversationRecordEditData> = new EventEmitter<ConversationRecordEditData>();

    protected form: FormGroup;
    protected visible: boolean = false;
    protected filteredDomains: GenericValue[];
    protected cancel = this.hideDialog;
    protected uiText = CommunicationLogUiText;
    protected overlayTitle: string;

    private _editedEntryId: number;
    protected get editedEntryId(): number {
        return this._editedEntryId;
    }
    protected set editedEntryId( value: number ) {
        this._editedEntryId = value;
        this.overlayTitle = this.getOverlayTitle( !!value );
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group( {
            title: ['', Validators.required],
            text: [''],
            domains: [null, Validators.required]
        } );
    }

    protected saveConversationRecord(): void {
        HisFormUtils.triggerFormValidation( this.form );
        if ( !this.form.valid ) {
            this.showValidationMessages();
            return;
        }
        const conversationRecord = new ConversationRecordEditData();
        conversationRecord.id = this.editedEntryId ?? undefined;
        conversationRecord.text = this.form.value.text;
        conversationRecord.title = this.form.value.title;
        conversationRecord.domainIds = this.form.value.domains?.map( ( domain: HisOption ) => parseInt( domain.id ) );
        this.conversationRecord.emit( conversationRecord );
    }

    private showValidationMessages(): void {
        const errorMessages: string[] = [];
        Object.keys( this.form.controls ).forEach( controlName => {
            const formControl = this.form.controls[controlName];
            if ( formControl.errors?.required ) {
                errorMessages.push( this.uiService.translate( this.uiText.COMMUNICATION_LOG_ENTRY_ATTRIBUTE_BASE + controlName ) + ': ' + this.uiService.translate( this.uiText.VALIDATION_REQUIRED ) );
            }
            if ( formControl.errors?.maxlength ) {
                errorMessages.push( this.uiService.translate( this.uiText.VALIDATION_MAX_LENGTH, this.uiService.translate( this.uiText.COMMUNICATION_LOG_ENTRY_ATTRIBUTE_BASE + controlName ), formControl.errors.maxlength.requiredLength ));
            }
        } );
        this.notificationService.clear();
        this.notificationService.error( errorMessages, this.uiService.translate( this.uiText.VIEW_MESSAGES_ERROR ) );
    }

    public hideDialog(): void {
        this.visible = false;
        this.editedEntryId = null;
        this.form.reset();
    }

    public showDialog(): void {
        this.form.reset();
        this.editedEntryId = null;
        if ( this.availableDomains?.length === 1 ) {
            this.form.setValue( {
                title: '',
                text: '',
                domains: this.availableDomains.map( domain => this.convertToHisOption( domain ) )
            } );
        }
        this.visible = true;
    }

    public editEntry( data: ConversationRecordEditData ): void {
        this.form.reset();
        const selectedDomains = data.domainIds
            .map( id => this.availableDomains.find( domain => domain.id === id ) )
            .map( domain => ( this.convertToHisOption( domain ) ) );

        this.form.setValue( {
            title: data.title ?? '',
            text: data.text ?? '',
            domains: selectedDomains
        } );
        this.editedEntryId = data.id;
        this.visible = true;
    }

    protected filterDomains( event: { query: string } ): void {
        const query = event.query;
        this.filteredDomains = this.availableDomains.filter( domain =>
            !this.form.value.domains?.includes( domain ) &&
            domain.uniquename?.toLowerCase().includes( query.toLowerCase() )
        );
    }

    protected getOverlayTitle( isEditMode: any ): string {
        const communicationRecord = this.uiService.translate( this.uiText.COMMUNICATION_LOG_CONVERSATIONRECORD );
        if ( isEditMode ) {
            return this.uiService.translate( this.uiText.EDIT_X, communicationRecord );
        }

        return this.uiService.translate( this.uiText.CREATE_X, communicationRecord );
    }

    protected customDomainOptions(): Observable<HisOption[]> {
        return of( this.availableDomains?.map( domain => ( this.convertToHisOption( domain ) ) ).sort( ( a, b ) => a.label.localeCompare( b.label ) ) );
    }

    private convertToHisOption( domain: GenericValue ): HisOption {
        return new HisOption( domain.longtext || domain.uniquename, `${domain.id}` );
    }
}